import dayjs from 'dayjs';
import { isEqual } from 'lodash-es';
import { PhoneAnalyticsTypes } from '@frontend/api-analytics';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { usePhoneAnalyticsDemoData } from './use-phone-analytics-demo-data';

const initialState = {
  accessibleLocationsNames: {} as Record<string, string>,
  defaultFilters: {} as PhoneAnalyticsTypes.Filters,
  demoData: null as ReturnType<typeof usePhoneAnalyticsDemoData> | null,
  filterHintText: '',
  filters: {} as PhoneAnalyticsTypes.Filters,
  hasCustomFilters: false,
  isDemoAccount: undefined as boolean | undefined,
  previousGlsLocationIds: '' as string | undefined,
};

type PhoneAnalyticsState = typeof initialState;

type InitialData = {
  accessibleLocationsNames: Record<string, string>;
  filterHintText: string;
  filters: PhoneAnalyticsTypes.Filters;
};

interface PhoneAnalyticsStore extends PhoneAnalyticsState {
  initialize: (initialData: InitialData) => void;
  resetToDefaults: () => void;
  setStore: (newState: Partial<PhoneAnalyticsStore>) => void;
}

const usePhoneAnalyticsStore = createStoreWithSubscribe<PhoneAnalyticsStore>(
  (set, get) => ({
    ...initialState,

    initialize: ({ accessibleLocationsNames, filterHintText, filters: newDefaultFilters }) => {
      const { defaultFilters: existingDefaultFilters } = get();

      // Only set the defaults if they haven't been set yet for the current locations.
      // This prevents re-initializing on every render but allows updates when GLS changes.
      if (!isEqual(existingDefaultFilters.location_id?.sort(), newDefaultFilters.location_id?.sort())) {
        set({
          defaultFilters: newDefaultFilters,
          filters: newDefaultFilters,
          hasCustomFilters: false,
          accessibleLocationsNames,
          filterHintText,
        });
      }
    },

    setStore: (newState) => {
      const currentState = get();

      // Safely merge incoming filters with existing ones
      if (newState.filters) {
        newState.filters = { ...currentState.filters, ...newState.filters };
      }

      const nextState = { ...currentState, ...newState };
      const { defaultFilters } = get();

      const areFiltersCustom = () => {
        const currentFilters = nextState.filters;
        if (!currentFilters || Object.keys(defaultFilters).length === 0) {
          return false;
        }

        // 1. Compare dates separately (this is a special case)
        const areDatesDifferent =
          !dayjs(currentFilters.start_date).isSame(dayjs(defaultFilters.start_date), 'day') ||
          !dayjs(currentFilters.end_date).isSame(dayjs(defaultFilters.end_date), 'day');
        if (areDatesDifferent) {
          return true;
        }

        // 2. Compare all other filter keys explicitly.
        const keysToCompare: (keyof PhoneAnalyticsTypes.Filters)[] = [
          'call_type',
          'location_id',
          'number_search',
          'open_office',
          'phone_number',
          'result',
          'source_type',
        ];

        for (const key of keysToCompare) {
          if (currentFilters[key] && !isEqual(currentFilters[key], defaultFilters[key])) {
            return true;
          }
        }

        return false;
      };

      nextState.hasCustomFilters = areFiltersCustom();
      set(nextState);
    },

    resetToDefaults: () => {
      const { defaultFilters } = get();

      set({
        filters: defaultFilters,
        hasCustomFilters: false,
      });
    },
  }),
  {
    name: 'PhoneAnalyticsStore',
    trace: true,
  }
);

export const usePhoneAnalyticsShallowStore = createShallowStore<PhoneAnalyticsStore>(usePhoneAnalyticsStore);
